import { values } from 'lodash';
import type { ValidationErrors } from '../../../../../utils/validation';
import { tcohV1Conclusie } from '../../../../../../common/customControls/tcohV1Conclusie';
import { translateKey } from '../../../../../translate';
import { Dropdown } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { TypeaheadInput } from '../../../../fields/InputTypeahead';

export interface ICustomControlData {
    advies: string | null;
    afwijkingen: string | null;
    conclusie: string | null;
    correlatieKlachten: string | null;
}

interface ITcohV1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: ICustomControlData;
    errors: ValidationErrors;
    handleChangeCustomControl: (values: Partial<ICustomControlData>) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function TcohV1DiagnosticReportAddEditCustomControlFields(
    props: ITcohV1DiagnosticReportAddEditCustomControlFieldsProps
) {
    const handleChangeCustomControl = async (value: Partial<ICustomControlData>) => {
        await props.handleChangeCustomControl(value);
    };

    const validateOneCustomControl = async (name: keyof ICustomControlData) => {
        await props.validateOneCustomControl(name);
    };

    return (
        <>
            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.conclusie')}>
                <FormLabel isRequired>Conclusie</FormLabel>
                <InputContainer>
                    <Dropdown
                        error={props.errors.get('customControlData.conclusie')}
                        onChange={async (value) => await handleChangeCustomControl({ conclusie: value })}
                        onDoValidate={async () => await validateOneCustomControl('conclusie')}
                        placeholder={translateKey('common.clickAndChooseFromList')}
                        selectOptions={diagnosticSelectOptions()}
                        value={props.customControlData.conclusie}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.correlatieKlachten')}>
                <FormLabel>Correlatie klachten</FormLabel>
                <InputContainer>
                    <TypeaheadInput
                        error={props.errors.get('customControlData.correlatieKlachten')}
                        onChange={async (value) => await handleChangeCustomControl({ correlatieKlachten: value })}
                        onDoValidate={async () => await validateOneCustomControl('correlatieKlachten')}
                        options={correlationComplaintsOptions()}
                        placeholder={translateKey('common.describeOrChooseFromList')}
                        value={props.customControlData.correlatieKlachten}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.afwijkingen')}>
                <FormLabel>ECG afwijkingen</FormLabel>
                <InputContainer>
                    <TypeaheadInput
                        error={props.errors.get('customControlData.afwijkingen')}
                        onChange={async (value) => await handleChangeCustomControl({ afwijkingen: value })}
                        onDoValidate={async () => await validateOneCustomControl('afwijkingen')}
                        options={deviationOptions()}
                        placeholder={translateKey('common.describeOrChooseFromList')}
                        value={props.customControlData.afwijkingen}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.advies')}>
                <FormLabel>Advies</FormLabel>
                <InputContainer>
                    <TypeaheadInput
                        error={props.errors.get('customControlData.advies')}
                        onChange={async (value) => await handleChangeCustomControl({ advies: value })}
                        onDoValidate={async () => await validateOneCustomControl('advies')}
                        options={adviceOptions()}
                        placeholder={translateKey('common.describeOrChooseFromList')}
                        value={props.customControlData.advies}
                    />
                </InputContainer>
            </HorizontalFormGroup>
        </>
    );
}

function convertToSelectOptions(options: string[]) {
    return values(options).map((value) => {
        return { value: value, text: value };
    });
}

function diagnosticSelectOptions() {
    return convertToSelectOptions(values(tcohV1Conclusie));
}

function correlationComplaintsOptions() {
    const complaints = [
        'Tijdens klachten wordt SV-ectopie gezien',
        'Tijdens klachten wordt V-ectopie gezien',
        'Tijdens klachten worden SV- en V-ectopie gezien',
        '‌Tijdens klachten worden ECG afwijkingen gezien. (HIER INVULLEN)',
        'Tijdens klachten worden geen ECG afwijkingen gezien',
        'De pati‌ë‌nt heeft geen cardiale klachten geuit',
        'De pati‌ë‌nt heeft een klacht aangegeven zonder duidelijk tijdstip',
    ];

    return complaints;
}

function deviationOptions() {
    const deviations = [
        'Tijdens gebruik van de event marker wordt SV-ectopie gezien',
        'Tijdens gebruik van de event marker wordt V-ectopie gezien',
        'Tijdens gebruik van de event marker worden SV- en V-ectopie gezien',
        'Tijdens gebruik van de event marker worden ECG afwijkingen gezien. (HIER INVULLEN)',
        'Tijdens gebruik van de event marker worden geen ECG afwijkingen gezien',
        'Er zijn wel ECG afwijkingen',
        'Er zijn geen ECG afwijkingen (SV ectopie binnen de norm)',
        'Er zijn geen ECG afwijkingen (V ectopie binnen de norm)',
        'Er zijn geen ECG afwijkingen (SV- en V-ectopie binnen de norm)',
        'Er zijn geen relevante ECG afwijkingen',
        'Er zijn borderline ECG afwijkingen',
    ];

    return deviations;
}

function adviceOptions() {
    const advices = [
        'Overleg cardioloog wordt geadviseerd',
        'Overweeg overleg cardioloog',
        'Overleg / consult cardioloog wordt geadviseerd',
        'Gelet op de aard en de mate van de SV ectopie, overweeg een consult cardioloog',
        'Gelet op de aard en de mate van de V ectopie, overweeg een consult cardioloog',
        'Gelet op de aard en de mate van de SV- en V-ectopie, overweeg een consult cardioloog',
        'Cardiologisch consult wordt met voorrang geïndiceerd',
        'Overweeg een 24-uurs Holter registratie om de hoeveelheid ectopie te kwantificeren',
    ];

    return advices;
}

export function tcohV1DiagnosticReportInitializeCustomControlData() {
    return {
        conclusie: null,
        correlatieKlachten: null,
        afwijkingen: null,
        advies: null,
    };
}
